'use client';

import { useCallback, useEffect, useState } from 'react';

const STORAGE_PREFIX = 'ocr-field-highlight-colors';

/** Overrides keyed by extracted-field `key` (shared across pages of one doc). */
export type FieldHighlightColors = Record<string, string>;

function storageKey(containerId: number): string {
  return `${STORAGE_PREFIX}:${containerId}`;
}

function read(containerId: number): FieldHighlightColors {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(storageKey(containerId));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (parsed == null || typeof parsed !== 'object') return {};
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).filter(
        (entry): entry is [string, string] =>
          typeof entry[1] === 'string' && /^#[0-9A-Fa-f]{6}$/.test(entry[1])
      )
    );
  } catch {
    return {};
  }
}

/**
 * Per-document highlight-color overrides for extracted OCR fields.
 *
 * Deliberately client-only: an operator recolors a field just to read the page
 * (or to download a highlighted PDF) for THIS container, and that must never
 * write back to the template's `ocr_template_custom_fields.color`, which every
 * other container using that template would inherit. Persisted in
 * localStorage, keyed by container, so a refresh doesn't lose the colors.
 */
export function useFieldHighlightColors(containerId: number) {
  const [colors, setColors] = useState<FieldHighlightColors>({});

  // Hydrate after mount — localStorage is unavailable during SSR, and reading
  // it in the initial state would desync the server/client render.
  useEffect(() => {
    setColors(read(containerId));
  }, [containerId]);

  const persist = useCallback(
    (containerKey: number, next: FieldHighlightColors) => {
      if (typeof window === 'undefined') return;
      try {
        if (Object.keys(next).length === 0) {
          window.localStorage.removeItem(storageKey(containerKey));
        } else {
          window.localStorage.setItem(
            storageKey(containerKey),
            JSON.stringify(next)
          );
        }
      } catch {
        // Private mode / quota — the colors just don't survive a refresh.
      }
    },
    []
  );

  const setFieldColor = useCallback(
    (fieldKey: string, color: string | null) => {
      setColors((prev) => {
        const next = { ...prev };
        if (color == null) {
          delete next[fieldKey];
        } else {
          next[fieldKey] = color;
        }
        persist(containerId, next);
        return next;
      });
    },
    [containerId, persist]
  );

  const resetAll = useCallback(() => {
    setColors({});
    persist(containerId, {});
  }, [containerId, persist]);

  return { colors, setFieldColor, resetAll };
}
